import React, { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { ListColumnType, ListFilterFieldType, ListMoreMenuConfig } from 'types/ListTypes';
import { BulkActionsConfig } from 'types/BulkActionTypes';
import AbstractList from 'components/lists/AbstractList';
import { useSettings } from 'services/settings/SettingsContext';
import { formatEmailListDate } from 'services/utils/dateTimeUtils';
import { renderTicketStatusBadge } from './TicketStatusBadge';
import TicketForm from './TicketForm';
import TicketConversationModal from './TicketConversationModal';
import { getTicketViewButtons } from './ticketViewButtons';
import '../../style/scss/tickets.scss';

// Enable the bulk actions panel and column selection for this list
export const ticketBulkActions: BulkActionsConfig = {
    objectKey: 'ticket',
    deleteOrDeactivate: 'delete',
    showExport: true,
};

// Configure the more menu of this list
export const ticketMoreMenu: ListMoreMenuConfig = {
    inactiveItemsToggle: {
        semantic: 'deleted',
        activeParams: { deleted: true },
    },
};

// The filter fields of the list
const ticketFilterFields: ListFilterFieldType[] = [
    {
        type: 'search',
        name: 'search',
        label: 'forms.search_label',
        placeholder: 'forms.search_placeholder',
        queryParam: 'search',
        width: 1.5,
    },
    {
        type: 'dropdown',
        name: 'category',
        label: 'ticket.general.category_label',
        queryParam: 'category',
        apiRequest: {
            endpoint: 'get_ticket_category_list',
            params: { deleted: false, is_active: true },
            object: 'ticket_category'
        },
        width: 1.25,
    },
    {
        type: 'dropdown',
        name: 'assignee',
        label: 'ticket.general.assignee_label',
        queryParam: 'assignee',
        optionField: 'full_name',
        apiRequest: {
            endpoint: 'get_user_list',
            params: { office_access: true, is_active: true },
            object: 'office_user'
        },
        width: 1.25,
    },
    {
        type: 'dropdown',
        name: 'division',
        label: 'ticket.general.division_label',
        queryParam: 'division',
        requiredFeature: 'divisions',
        defaultValueFromContext: 'user_division',
        apiRequest: {
            endpoint: 'get_division_list',
            params: { is_active: true },
            object: 'division'
        },
        width: 1.5,
    },
];

// The query of the list, shows every request of the environment
const ticketQuery = {
    endpoint: 'get_ticket_list',
    defaultParams: { deleted: false },
    object: 'ticket'
};

const TicketList: React.FC = () => {
    const history = useHistory();
    const { userLocale, userTimezone } = useSettings();

    // Specify the columns of this list
    const columns: ListColumnType[] = useMemo(() => [
        {
            width: '1.25fr',
            field: 'number',
            label: 'ticket.general.number_label'
        },
        {
            width: '3fr',
            field: 'reporter_display_name',
            orderField: 'reporter__name',
            label: 'ticket.general.reporter_label'
        },
        {
            width: '6fr',
            field: 'subject',
            label: 'ticket.general.subject_label',
            customClass: (_: any, row: any) => row.new_reply ? 'unread' : ''
        },
        {
            width: '2.5fr',
            field: 'assignee_name',
            orderField: 'assignee__full_name',
            label: 'ticket.general.assignee_label'
        },
        {
            width: '2fr',
            field: 'last_message_at',
            label: 'ticket.general.last_message_label',
            render: (_, row) => <>{row.last_message_at ? formatEmailListDate(row.last_message_at, userLocale, userTimezone) : ''}</>
        },
        {
            width: '144px',
            field: 'status',
            label: 'ticket.general.status_label',
            render: (_, row) => renderTicketStatusBadge(row.status, row.new_reply)
        }
    ], [userLocale, userTimezone]);

    return (
        <div className='ticket-list'>
            <AbstractList
                query={ticketQuery}
                objectName='ticket.general.object_name'
                extendedWidth={true}
                columns={columns}
                filterFields={ticketFilterFields}
                formModal={<TicketForm />}
                modalSize='add-form'
                changeViewButton={getTicketViewButtons(history, 'list')}
                isPaginated={true}
                onRowClick='detailModal'
                detailModal={<TicketConversationModal />}
                detailModalSize='large'
                bulkActions={ticketBulkActions}
                moreMenu={ticketMoreMenu}
            />
        </div>
    );
};

export default TicketList;
